import type { AppDispatch, RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { type ColumnOption } from "../../../../../../interfaces/columnOptions";
import { Loader, Table } from "semantic-ui-react";
import { RolePermissions, SortingDirection, ViewType } from "enums";
import { useEffect, useMemo, useRef, useState } from "react";
import { ModalTypes, RestrictedByTooltip } from "components";
import ModalManager from "utils/ModalManager";
import { bindAction, type IModalManager } from "interfaces";
import { reset } from "../../state/slices/emailCancelSendSlice";
import { noop } from "lodash";
import { cancelEmailSend } from "../../state/thunks/emailSendsThunk";
import emailsDataService from "../../services/emailsDataService";
import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { ItemsView } from "views";
import { CancelSendSessionModal } from "../../SendSessions/CancelSendSessionModal/CancelSendSessionModal";
import type { ScheduledSendSessions } from "../../types/state";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleX, faExclamationCircle, faPaperPlane } from "@fortawesome/pro-solid-svg-icons";
import NoResults from "components/noResults/NoResults";
import cn from "classnames";
import moment from "moment";
import { useGetLastUpdatedTimeQuery } from "../../state/api/sendSessionsApi";

import styles from "./sends.module.scss";
import { Tooltip } from "components/common/tooltip";
import { showStatus } from "../../utils/emailUtils";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";

export type Props = {
  entityId: number;
  createButton: React.ReactElement;
  selectedSends: { id: number; status: "canceled" | "scheduled" }[];
  isDraft: boolean;
  onChangeSelectedSendSessions: (selectedSends: { id: number; status: "canceled" | "scheduled" }[]) => void;
} & PropsFromRedux;

type queryFilter = QueryFunctionContext<[_: string, entityId: number]>;

const fetchData = async ({ queryKey }: queryFilter) => {
  const results = (await emailsDataService.getSendSessions(queryKey[1])).data;
  return results.map(({ SendSessionId, ...rest }) => ({
    ...rest,
    id: SendSessionId,
  }));
};

const itemsPerPage = 10;

const Sends = (props: Props) => {
  const { entityId, createButton, selectedSends, resetCancelSend, cancelSend, userPermissions, isDraft } = props;
  const [pagination, setPagination] = useState(1);
  const [sortingDirection, setSortingDirection] = useState(SortingDirection.Descending);
  const formatDateTime = useReportingFormatWithTimeZone();

  const sendSessionQuery = useQuery({
    queryKey: ["emailSendSessions", entityId],
    queryFn: fetchData,
  });

  const { isSuccess, data: lastUpdatedData } = useGetLastUpdatedTimeQuery();

  const modalManager = useRef<null | IModalManager>(null);
  const onCancelConfirm = useRef(noop);

  const onSortingDirectionChange = (direction: SortingDirection) => setSortingDirection(direction);

  useEffect(() => {
    modalManager.current = new ModalManager(ModalTypes.CancelEmailSend, () => onCancelConfirm.current());
    return () => {
      resetCancelSend();
      modalManager.current!.dispose();
    };
  }, [resetCancelSend]);

  const columnOptions: ColumnOption<ScheduledSendSessions>[] = [
    {
      name: "Scheduled Time",
      width: 5,
      isSortable: true,
      render: (item: ScheduledSendSessions) => formatDateTime(item.SendSessionTimestamp),
    },
    {
      name: "Status",
      width: 11,
      render: showStatus,
    },
    {
      name: "",
      width: 1,
      isSortable: false,
      render: (entity) => {
        const hasSendCancellationPermission =
          !isDraft && userPermissions.includes(RolePermissions.CommunicationsManage);
        const isSendDisabled =
          selectedSends.some((email) => email.id === entity.id) ||
          isDraft ||
          !userPermissions.includes(RolePermissions.CommunicationsManage);

        return (
          <>
            {entity.Status !== "canceled" && (
              <RestrictedByTooltip hasPermission={hasSendCancellationPermission}>
                <Tooltip
                  target={
                    <FontAwesomeIcon
                      icon={faCircleX}
                      className={cn(styles.cancelIcon, {
                        [styles.disabled]: isSendDisabled,
                      })}
                      data-testid="cancel-send-icon"
                      onClick={() => {
                        if (hasSendCancellationPermission) onCancelClick(entity);
                      }}
                    />
                  }
                  content={"Cancel Send"}
                  showAlways={hasSendCancellationPermission}
                  position={"left center"}
                />
              </RestrictedByTooltip>
            )}
          </>
        );
      },
    },
  ];

  const onCancelClick = (sendToCancel: ScheduledSendSessions) => {
    onCancelConfirm.current = () => {
      cancelSend(sendToCancel.id);
    };
    modalManager.current!.execute();
  };

  const buildTableBody = (listItem: ScheduledSendSessions) => {
    const cancelButtonColumn = 2;
    return (
      <>
        {columnOptions.map((item, index) => (
          <Table.Cell
            width={item.width}
            key={index}
            disabled={selectedSends.some((email) => email.id === listItem.id) && cancelButtonColumn === index}
          >
            {item.render(listItem)}
          </Table.Cell>
        ))}
      </>
    );
  };

  const noResults = () => (
    <div className={styles.noResults}>
      <NoResults
        title="You don't have any scheduled sends"
        description="You don't have any scheduled sends yet. Create one now using the button below."
        icon={<FontAwesomeIcon icon={faPaperPlane} className={`no-results-icon ${styles.icon}`} />}
      >
        {createButton}
      </NoResults>
    </div>
  );

  const onPaginationChange = (page: number) => setPagination(page);

  const sortedData = useMemo(() => {
    return sendSessionQuery.data?.sort((a, b) => {
      if (sortingDirection === SortingDirection.Ascending) {
        return moment(b.SendSessionTimestamp).valueOf() - moment(a.SendSessionTimestamp).valueOf();
      } else {
        return moment(a.SendSessionTimestamp).valueOf() - moment(b.SendSessionTimestamp).valueOf();
      }
    });
  }, [sendSessionQuery.data, sortingDirection]);

  const paginatedSends = useMemo(
    () => sortedData?.slice((pagination - 1) * itemsPerPage, pagination * itemsPerPage),
    [sortedData, pagination],
  );

  const lastUpdatedSection = useMemo(() => {
    if (isSuccess && lastUpdatedData) {
      return (
        <div className={styles.lastUpdatedInfo}>
          <strong>
            <FontAwesomeIcon className={styles.lastUpdatedIcon} icon={faExclamationCircle} color="yellow" /> Last
            updated: {formatDateTime(lastUpdatedData.LastUpdated)}.
          </strong>
          <span>Scheduled email sends may take up to 15 minutes to appear.</span>
        </div>
      );
    }
    return null;
  }, [isSuccess, lastUpdatedData, formatDateTime]);

  return (
    <>
      {sendSessionQuery.isLoading ? (
        <Loader active />
      ) : (
        <div className={styles.root}>
          <ItemsView
            aboveBodyContent={lastUpdatedSection}
            getData={() => sendSessionQuery}
            items={paginatedSends}
            itemsInlineCount={sendSessionQuery.data?.length}
            onLoad={() => sendSessionQuery}
            data-testid="sends-list"
            className="sends-list"
            columnOptions={columnOptions}
            buildTableBody={buildTableBody}
            viewType={ViewType.LIST}
            isLoading={sendSessionQuery.isLoading}
            sortingColumnName={"Scheduled Time"}
            sortingDirection={sortingDirection}
            setSortingDirection={onSortingDirectionChange}
            noResultsContent={noResults()}
            onSelectedListItemsChanged={(ids: number[]) => {
              const sendsWithData =
                sendSessionQuery.data
                  ?.filter((send) => ids.includes(send.id))
                  .map((send) => ({
                    id: send.id,
                    status: send.Status,
                  })) || [];
              return props.onChangeSelectedSendSessions(sendsWithData);
            }}
            withFooter={false}
            setPagination={onPaginationChange}
            selectedIds={selectedSends.map((send) => send.id)}
          />
          {modalManager.current && <CancelSendSessionModal modalManager={modalManager.current} />}
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  userPermissions: state.userProfile.permissions,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  resetCancelSend: bindAction(reset, dispatch),
  cancelSend: bindAction(cancelEmailSend, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(Sends);
export default ConnectedComponent;
